import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';

const ProfileSection = () => {
  const [profileData, setProfileData] = useState({
    name: '',
    weddingDate: '',
    location: '',
    profilePhoto: null
  });

  const [previewImage, setPreviewImage] = useState(null);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result);
      };
      reader.readAsDataURL(file);
      setProfileData(prev => ({
        ...prev,
        profilePhoto: file
      }));
    }
  };

  const locationSuggestions = [
    'New York, NY',
    'Los Angeles, CA',
    'Chicago, IL',
    'Houston, TX',
    'Phoenix, AZ',
    'Philadelphia, PA',
    'San Antonio, TX',
    'San Diego, CA'
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Profile</h1>
          <p className="text-text-muted mt-1">Manage your wedding profile information</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            className="hidden sm:flex"
          >
            Preview
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconName="Save"
          >
            Save
          </Button>
        </div>
      </div>

      {/* Profile Photo Section */}
      <div className="bg-surface rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Profile Photo</h2>
        <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="relative group">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center overflow-hidden border-4 border-primary-50 shadow-soft">
              {previewImage ? (
                <img 
                  src={previewImage} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={40} color="#8B1538" />
              )}
            </div>
            <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <Icon name="Camera" size={24} color="white" />
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-medium text-text-primary mb-2">Upload your photo</h3>
            <p className="text-sm text-text-muted mb-4">
              Choose a clear, recent photo that represents you well. JPG or PNG format recommended.
            </p>
            <label className="inline-block">
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                className="cursor-pointer"
              >
                Choose Photo
              </Button>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-surface rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Full Name *
            </label>
            <div className="relative">
              <Icon 
                name="User" 
                size={18} 
                color="#9E9E9E" 
                className="absolute left-3 top-1/2 transform -translate-y-1/2" 
              />
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter your full name"
                className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Wedding Date Field */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Wedding Date *
            </label>
            <div className="relative">
              <Icon 
                name="Calendar" 
                size={18} 
                color="#9E9E9E" 
                className="absolute left-3 top-1/2 transform -translate-y-1/2" 
              />
              <input
                type="date"
                value={profileData.weddingDate}
                onChange={(e) => handleInputChange('weddingDate', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors"
              />
            </div>
            <p className="text-xs text-text-muted mt-1">
              Cultural calendars supported for different traditions
            </p>
          </div>
        </div>

        {/* Location Field */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Wedding Location *
          </label>
          <div className="relative">
            <Icon 
              name="MapPin" 
              size={18} 
              color="#9E9E9E" 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10" 
            />
            <input
              type="text"
              value={profileData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="Enter city, state"
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors"
              list="location-suggestions"
            />
            <datalist id="location-suggestions">
              {locationSuggestions.map((location) => (
                <option key={location} value={location} />
              ))}
            </datalist>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Start typing to see city suggestions
          </p>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-surface rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Additional Details</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              About Your Wedding
            </label>
            <textarea
              rows={4}
              placeholder="Tell us about your vision, traditions, or any special details about your wedding..."
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Wedding Size
              </label>
              <select className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors">
                <option value="">Select size</option>
                <option value="intimate">Intimate (1-50 guests)</option>
                <option value="medium">Medium (51-150 guests)</option>
                <option value="large">Large (151-300 guests)</option>
                <option value="grand">Grand (300+ guests)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Cultural Tradition
              </label>
              <select className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary transition-colors">
                <option value="">Select tradition</option>
                <option value="hindu">Hindu</option>
                <option value="sikh">Sikh</option>
                <option value="muslim">Muslim</option>
                <option value="christian">Christian</option>
                <option value="buddhist">Buddhist</option>
                <option value="mixed">Mixed/Fusion</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Save Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 bg-surface rounded-xl p-6 border border-border">
        <div className="text-center sm:text-left">
          <p className="text-sm text-text-muted">
            Your profile helps us connect you with the right vendors
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="ghost"
            size="sm"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconName="Save"
          >
            Save Profile
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProfileSection;